import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth, usePageTitle } from '../App';
import { Button } from '../components/ui/button';
import { ArrowLeft, ExternalLink, BookOpen, Bookmark, BookmarkCheck, Users } from 'lucide-react';
import { toast } from 'sonner';
import WholePictureCard from '../components/WholePictureCard';
import { useTrackProgress } from '../hooks/useTrackProgress';

export default function PersonDetailPage() {
  const { personId } = useParams();
  const { user } = useAuth();
  const [person, setPerson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkId, setBookmarkId] = useState(null);

  // Track progress
  useTrackProgress('person', personId);
  usePageTitle(person?.name);

  useEffect(() => {
    const fetchPerson = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/people/${personId}`);
        setPerson(res.data);
      } catch (error) {
        console.error('Error fetching person:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPerson();
  }, [personId]);

  // Check if bookmarked
  useEffect(() => {
    if (!user) return;
    const checkBookmark = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/bookmarks`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        });
        const bookmark = res.data.find(b => b.item_type === 'person' && b.item_id === personId);
        if (bookmark) {
          setIsBookmarked(true);
          setBookmarkId(bookmark.bookmark_id);
        }
      } catch (error) {
        console.error('Error checking bookmark:', error);
      }
    };
    checkBookmark();
  }, [user, personId]);

  const handleBookmark = async () => {
    if (!user) {
      toast.error('Please sign in to bookmark');
      return;
    }
    
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    try {
      if (isBookmarked) {
        await axios.delete(`${API}/bookmarks/${bookmarkId}`, { headers, withCredentials: true });
        setIsBookmarked(false);
        setBookmarkId(null);
        toast.success('Bookmark removed');
      } else {
        const res = await axios.post(`${API}/bookmarks`, 
          { item_type: 'person', item_id: personId },
          { headers, withCredentials: true }
        );
        setIsBookmarked(true);
        setBookmarkId(res.data.bookmark_id);
        toast.success('Bookmarked!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating bookmark');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-12 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="skeleton h-8 w-32 mb-8" />
          <div className="skeleton h-64 rounded-sm mb-8" />
          <div className="skeleton h-4 w-3/4 mb-4" />
          <div className="skeleton h-4 w-1/2" />
        </div>
      </div>
    );
  }

  if (!person) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-white/50">Person not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4 md:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back button */}
        <Link to="/people" className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" />
          <span className="font-mono text-sm uppercase tracking-wider">Back to People</span>
        </Link>

        {/* Header */}
        <div className="card-dark p-8 mb-8" data-testid="person-detail">
          <div className="flex flex-col md:flex-row items-start gap-8">
            {/* Avatar */}
            <div className="w-32 h-32 rounded-full overflow-hidden flex-shrink-0 border-2 border-white/10">
              {person.image_url ? (
                <img src={person.image_url} alt={person.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-white/5 flex items-center justify-center text-white/40 text-4xl font-heading">
                  {person.name.charAt(0)}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h1 className="font-heading text-3xl md:text-4xl font-bold mb-2">{person.name}</h1>
                  <p className="text-cyan-400 font-mono">{person.role}</p>
                </div>
                <Button
                  variant="ghost"
                  onClick={handleBookmark}
                  className="text-white/60 hover:text-white"
                  data-testid="bookmark-btn"
                >
                  {isBookmarked ? (
                    <BookmarkCheck className="w-6 h-6 text-cyan-400" />
                  ) : (
                    <Bookmark className="w-6 h-6" />
                  )}
                </Button>
              </div>

              <p className="text-white/70 leading-relaxed mb-6">{person.bio}</p>

              {/* Expertise */}
              {person.expertise?.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-mono text-xs uppercase tracking-wider text-white/40 mb-3">Expertise</h3>
                  <div className="flex flex-wrap gap-2">
                    {person.expertise.map(exp => (
                      <span key={exp} className="px-3 py-1 text-sm bg-white/5 border border-white/10 rounded-sm">
                        {exp}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Share Button */}
              <div className="pt-4 border-t border-white/10">
                <WholePictureCard 
                  type="person"
                  data={{
                    name: person.name,
                    role: person.role,
                    bio: person.bio,
                    image: person.image_url
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Connections */}
        {person.connections?.length > 0 && (
          <div className="card-dark p-6 mb-8">
            <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-400" />
              Connected To
            </h2>
            <div className="flex flex-wrap gap-3">
              {person.connections.map(conn => (
                <Link
                  key={conn}
                  to={`/people/${conn}`}
                  className="px-4 py-2 bg-white/5 border border-white/10 rounded-sm hover:border-cyan-400/50 transition-colors"
                >
                  {conn.replace('person_', '').replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Key Works */}
        {person.key_works?.length > 0 && (
          <div className="card-dark p-6 mb-8">
            <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-cyan-400" />
              Key Works
            </h2>
            <ul className="space-y-2">
              {person.key_works.map((work, idx) => (
                <li key={idx} className="text-white/70 flex items-center gap-2">
                  <span className="text-cyan-400">•</span>
                  {work}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Sources */}
        {person.sources?.length > 0 && (
          <div className="card-dark p-6">
            <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-cyan-400" />
              Sources
            </h2>
            <ul className="space-y-2">
              {person.sources.map((source, idx) => (
                <li key={idx}>
                  {source.startsWith('http') ? (
                    <a href={source} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline break-all">
                      {source}
                    </a>
                  ) : (
                    <span className="text-white/70">{source}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
